import { useSortable } from "@dnd-kit/sortable";
import { flexRender, Header } from "@tanstack/react-table";
import clsx from "clsx";

import { TableMetaProps } from "@/hooks/table/useTableConfig";

type TableHeaderCellProps<T> = {
  header: Header<T, unknown>;
  index: number;
  canReorderColumns: boolean;
};

const TableHeaderCell = <T,>({ header, index, canReorderColumns }: TableHeaderCellProps<T>) => {
  const meta: TableMetaProps = header.column.columnDef.meta || {};
  const Icon = meta.heading?.icon || null;

  const { attributes, isDragging, listeners, setNodeRef, isOver } = useSortable({
    id: `column-${header.id}`,
    disabled: !canReorderColumns,
  });

  const style = {
    // Fixed to 300px to prevent jumping columns with virtualization
    minWidth: "300px", //`${header.getSize()}px`,
  };

  return (
    <th
      colSpan={header.colSpan}
      ref={!meta.disableReorder && canReorderColumns ? setNodeRef : undefined}
      style={style}
      className={clsx(
        "relative flex flex-1 select-none border-b border-r border-b-primary border-r-secondary bg-secondary px-4 py-2.5 text-start text-sm font-medium text-tertiary last:border-r-0 hover:bg-secondary-hover active:bg-secondary-hover",
        index === 0 && "sticky left-0 !z-40",
        isDragging && "opacity-80",
        isOver && "border-r-4 border-r-brand-primary",
        meta.heading?.className,
      )}
    >
      <div className={"flex w-full"}>
        <div className={"group flex-1"}>
          {header.isPlaceholder ? null : (
            <div
              className={clsx("flex items-center gap-1.5 truncate", canReorderColumns && "cursor-grab")}
              {...attributes}
              {...listeners}
            >
              {Icon && <Icon className="size-4 shrink-0 stroke-2 text-quaternary" />}
              {meta.heading?.title}
              {flexRender(header.column.columnDef.header, header.getContext())}
            </div>
          )}
        </div>
      </div>
    </th>
  );
};

export default TableHeaderCell;
